import * as Y from "yjs";
import { type Signal, signal } from "../lib/signal.js";

export type SyncStatus = "connecting" | "online" | "offline";

export type SyncOptions = {
  url: string;
  workspaceId: string;
  docId: string;
  doc: Y.Doc;
};

const RECONNECT_MS = [200, 400, 800, 1500, 3000, 5000];

/** The wire format, stated once. The relay's half is apps/edge/src/protocol.ts. */
const TAG_BATCH = 0x01;
const TAG_UPDATE = 0x02;

/**
 * A Yjs provider over the relay (§4.2, §7 phase 3).
 *
 * A plain WebSocket, because the relay is a Durable Object that stores opaque
 * blobs and hands them back. It cannot compute a delta — it cannot read the
 * payloads (§4.5) — so the handshake is inverted from the usual one: on connect
 * the relay sends the entire log for this document in one framed batch, the
 * client merges it, derives the state vector *the relay's bytes imply*, and
 * sends back only what that vector is missing.
 *
 * All the CRDT reasoning is therefore here, which is exactly where §4.5 wants
 * it — and why moving the relay off Phoenix changed this file's transport and
 * none of its logic.
 */
export class SyncProvider {
  readonly status: Signal<SyncStatus>;
  /** Bumped every time a batch from the relay has been merged. */
  readonly synced: Signal<number>;
  /** Resolves once the relay's log has been merged at least once. */
  readonly firstSync: Promise<void>;

  readonly #doc: Y.Doc;
  readonly #url: string;
  readonly #onUpdate: (update: Uint8Array, origin: unknown) => void;
  #socket: WebSocket | null = null;
  #attempt = 0;
  #reconnect: ReturnType<typeof setTimeout> | null = null;
  #destroyed = false;
  #resolveFirstSync: (() => void) | null = null;

  constructor(options: SyncOptions) {
    this.#doc = options.doc;
    this.#url = `${options.url}/${options.workspaceId}/${options.docId}`;
    this.status = signal<SyncStatus>("connecting");
    this.synced = signal(0);
    this.firstSync = new Promise<void>((resolve) => {
      this.#resolveFirstSync = resolve;
    });

    // Local changes go out; changes that arrived from the relay do not go back.
    this.#onUpdate = (update: Uint8Array, origin: unknown): void => {
      if (origin === this) return;
      this.#send(update);
    };
    this.#doc.on("update", this.#onUpdate);

    this.#connect();
  }

  destroy(): void {
    this.#destroyed = true;
    this.#doc.off("update", this.#onUpdate);
    if (this.#reconnect !== null) clearTimeout(this.#reconnect);
    this.#reconnect = null;
    const socket = this.#socket;
    this.#socket = null;
    if (socket !== null) {
      socket.onclose = null;
      socket.onerror = null;
      socket.onmessage = null;
      socket.close();
    }
  }

  #connect(): void {
    if (this.#destroyed) return;
    this.status.set("connecting");

    const socket = new WebSocket(this.#url);
    socket.binaryType = "arraybuffer";
    this.#socket = socket;

    socket.onopen = () => {
      this.#attempt = 0;
    };
    socket.onmessage = (event: MessageEvent) => {
      if (typeof event.data === "string") {
        // Control messages are the relay's only way to say no. There is nothing
        // useful to do about one here, but silence would make an update the
        // relay refused look like one it accepted.
        console.error("[sync] relay refused a message:", event.data);
        return;
      }
      this.#receive(event.data as ArrayBuffer);
    };
    socket.onerror = () => {
      if (!this.#destroyed) this.status.set("offline");
    };
    socket.onclose = () => {
      if (this.#socket !== socket) return;
      this.#socket = null;
      if (this.#destroyed) return;
      this.status.set("offline");
      this.#scheduleReconnect();
    };
  }

  #scheduleReconnect(): void {
    if (this.#destroyed || this.#reconnect !== null) return;
    const delay = RECONNECT_MS[Math.min(this.#attempt, RECONNECT_MS.length - 1)] ?? 5000;
    this.#attempt += 1;
    this.#reconnect = setTimeout(() => {
      this.#reconnect = null;
      this.#connect();
    }, delay);
  }

  #receive(buffer: ArrayBuffer): void {
    const bytes = new Uint8Array(buffer);
    if (bytes.byteLength === 0) return;

    if (bytes[0] === TAG_UPDATE) {
      Y.applyUpdate(this.#doc, bytes.subarray(1), this);
      return;
    }
    if (bytes[0] !== TAG_BATCH) {
      console.error("[sync] unknown frame tag", bytes[0]);
      return;
    }

    let updates: Uint8Array[];
    try {
      updates = decodeBatch(buffer);
    } catch (error) {
      console.error("[sync] malformed batch from the relay", error);
      return;
    }
    this.#merge(updates);
  }

  /**
   * The relay's whole log for this document.
   *
   * Merging first and comparing state vectors afterwards means a client that
   * has been offline for a week sends exactly the operations the relay lacks —
   * not its whole document, and not nothing.
   */
  #merge(updates: Uint8Array[]): void {
    let relayVector: Uint8Array | null = null;
    let relayState = new Map<number, number>();
    if (updates.length > 0) {
      const merged = Y.mergeUpdates(updates);
      relayVector = Y.encodeStateVectorFromUpdate(merged);
      relayState = Y.decodeStateVector(relayVector);
      Y.applyUpdate(this.#doc, merged, this);
    }

    // Compare the decoded vectors rather than their encodings: the byte form
    // depends on map iteration order and is not a reliable equality test.
    const ours = Y.decodeStateVector(Y.encodeStateVector(this.#doc));
    let ahead = false;
    for (const [client, clock] of ours) {
      if ((relayState.get(client) ?? 0) < clock) {
        ahead = true;
        break;
      }
    }

    this.status.set("online");
    if (ahead) {
      this.#send(
        relayVector === null
          ? Y.encodeStateAsUpdate(this.#doc)
          : Y.encodeStateAsUpdate(this.#doc, relayVector),
      );
    }
    this.synced.set(this.synced.peek() + 1);
    this.#resolveFirstSync?.();
    this.#resolveFirstSync = null;
  }

  #send(update: Uint8Array): void {
    const socket = this.#socket;
    // Offline is not an error: the edit is already in the document and in
    // IndexedDB, and the next connection's handshake will carry it.
    if (socket === null || socket.readyState !== WebSocket.OPEN || this.#destroyed) return;
    const framed = new Uint8Array(update.byteLength + 1);
    framed[0] = TAG_UPDATE;
    framed.set(update, 1);
    socket.send(framed);
  }
}

/** `0x01` then `(u32 length, payload)*` — the framing the relay sends. */
export function decodeBatch(buffer: ArrayBuffer): Uint8Array[] {
  const view = new DataView(buffer);
  const out: Uint8Array[] = [];
  let offset = 1; // past the tag
  while (offset < buffer.byteLength) {
    if (offset + 4 > buffer.byteLength) throw new Error("truncated length prefix");
    const length = view.getUint32(offset);
    offset += 4;
    if (offset + length > buffer.byteLength) throw new Error("truncated payload");
    out.push(new Uint8Array(buffer, offset, length));
    offset += length;
  }
  return out;
}

/**
 * Where the relay lives.
 *
 * Always same-origin now: the Worker serves the shell *and* the relay, so there
 * is no second host to configure and no dev-only special case beyond Vite's own
 * ports.
 */
export function defaultSyncUrl(): string {
  const configured: unknown = import.meta.env["VITE_SYNC_URL"];
  if (typeof configured === "string" && configured !== "") return configured;

  const { protocol, hostname, host, port } = window.location;
  const scheme = protocol === "https:" ? "wss:" : "ws:";
  const isLocal = hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
  // Vite's dev (5173) and preview (4173) servers do not host the relay;
  // `wrangler dev` does, on 8787.
  if (isLocal && (port === "5173" || port === "4173")) return "ws://localhost:8787/sync";
  return `${scheme}//${host}/sync`;
}
